import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getMediatorOverview,
  EngineError,
  type MediatorOverview,
  type OverviewItem,
  type OverviewSession,
} from '../../api/engineDashboard';

/**
 * "Current overview" — the briefing at the very top of Mission Control
 * (Danny, 2026-11-04). Plain, prioritized sentences: today's sessions,
 * then exactly what needs the mediator, highest priority first. All copy
 * is deterministic templates over the engine's structured items — the
 * section never states anything that isn't in the data.
 *
 * Deliberately SHORT (the "Upcoming" panel was demoted for eating the
 * page): ≤7 items visible, grouped sentences, no tabs, no second agenda.
 */

interface Props {
  token: string;
  selfName: string;
  onOpenCase: (caseId: string) => void;
  onShowUnassigned?: () => void;
  onUnauthorized: () => void;
}

const VISIBLE_ITEMS = 7;
const VISIBLE_GROUP_CASES = 5;

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const countWord = (n: number): string => (n < NUMBER_WORDS.length ? NUMBER_WORDS[n] : String(n));

function durationPhrase(minutes: number): string {
  if (minutes % 60 === 0) {
    const h = minutes / 60;
    return h === 1 ? '1-hour' : `${h}-hour`;
  }
  return `${minutes}-minute`;
}

function dayPart(iso: string, timeZone: string): 'morning' | 'afternoon' | 'evening' {
  const h = Number(
    new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', hour12: false }).format(new Date(iso)),
  ) % 24;
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function timeLabel(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, hour: 'numeric', minute: '2-digit' }).format(new Date(iso));
}

/** "two 45-minute mediations this morning and a 2-hour mediation this evening" */
function sessionsSentence(sessions: OverviewSession[], timeZone: string): string | null {
  if (sessions.length === 0) return null;
  const live = sessions.filter((s) => s.status === 'in_progress');
  const upcoming = sessions.filter((s) => s.status !== 'in_progress');

  const groups = new Map<string, { count: number; dur: string; part: string }>();
  for (const s of upcoming) {
    const key = `${durationPhrase(s.duration_minutes)}|${dayPart(s.scheduled_start, timeZone)}`;
    const g = groups.get(key);
    if (g) g.count += 1;
    else groups.set(key, { count: 1, dur: durationPhrase(s.duration_minutes), part: dayPart(s.scheduled_start, timeZone) });
  }

  let body: string;
  if (groups.size === 0) {
    body = '';
  } else if (groups.size > 3) {
    // Heterogeneous day → plain time list beats a tangled sentence.
    body = upcoming
      .map((s) => `${timeLabel(s.scheduled_start, timeZone)} — ${s.case_label} (${s.duration_minutes} min)`)
      .join('; ');
    body = `Today: ${body}.`;
  } else {
    const parts = [...groups.values()].map((g) => {
      const noun = g.count === 1 ? 'mediation' : 'mediations';
      const article = g.count === 1 ? (/^[18]/.test(g.dur) ? 'an' : 'a') : countWord(g.count);
      return `${article} ${g.dur} ${noun} this ${g.part}`;
    });
    const joined = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    body = `Today you have ${joined}.`;
  }

  const liveNote = live.length
    ? ` A ${durationPhrase(live[0].duration_minutes)} mediation is happening now.`
    : '';
  return `${body}${liveNote}`.trim() || null;
}

function CaseLink({ id, label, onOpen }: { id: string; label: string; onOpen: (id: string) => void }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(id)}
      className="font-medium text-brand underline decoration-brand/40 underline-offset-2 hover:decoration-brand"
    >
      {label}
    </button>
  );
}

function CaseLinkList({ item, onOpen }: { item: OverviewItem; onOpen: (id: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? item.cases : item.cases.slice(0, VISIBLE_GROUP_CASES);
  const hidden = item.cases.length - shown.length;
  return (
    <>
      {shown.map((c, i) => (
        <span key={c.id}>
          {i > 0 && (i === shown.length - 1 && hidden === 0 ? ' and ' : ', ')}
          <CaseLink id={c.id} label={c.label} onOpen={onOpen} />
        </span>
      ))}
      {hidden > 0 && (
        <>
          {' '}
          <button type="button" className="text-brand hover:underline" onClick={() => setExpanded(true)}>
            and {hidden} more
          </button>
        </>
      )}
    </>
  );
}

export default function CurrentOverview({ token, selfName, onOpenCase, onShowUnassigned, onUnauthorized }: Props) {
  const [overview, setOverview] = useState<MediatorOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    setError(null);
    getMediatorOverview(token)
      .then(setOverview)
      .catch((e) => {
        if (e instanceof EngineError && e.status === 401) {
          onUnauthorized();
          return;
        }
        setError('Couldn’t load your briefing.');
      });
  }, [token, onUnauthorized]);

  useEffect(() => {
    load();
  }, [load]);

  const firstName = selfName.trim().split(/\s+/)[0] || 'there';

  const openMessages = useCallback(
    (id: string) => {
      sessionStorage.setItem('lh-scroll-messages', id);
      onOpenCase(id);
    },
    [onOpenCase],
  );
  const openScheduling = useCallback(
    (id: string) => {
      sessionStorage.setItem('lh-scroll-scheduling', id);
      onOpenCase(id);
    },
    [onOpenCase],
  );

  // One deterministic sentence per item type. Copy rule: say what to DO.
  const renderItem = useCallback(
    (item: OverviewItem) => {
      const one = item.cases[0];
      switch (item.type) {
        case 'unanswered_question':
          return (
            <>
              Case <CaseLink id={one.id} label={one.label} onOpen={openMessages} /> has a message waiting on your reply
              {Number(item.data.days) >= 1 ? ` (${item.data.days} day${Number(item.data.days) === 1 ? '' : 's'})` : ''}.
            </>
          );
        case 'internal_question':
          return (
            <>
              The court team wrote on case <CaseLink id={one.id} label={one.label} onOpen={openMessages} /> — unread
              internal message.
            </>
          );
        case 'call_party':
          return (
            <>
              Please call {String(item.data.party_name)} on case{' '}
              <CaseLink id={one.id} label={one.label} onOpen={openScheduling} /> — they haven’t responded to scheduling
              messages.
            </>
          );
        case 'missing_info':
          return (
            <>
              {item.cases.length === 1 ? 'Case ' : 'Cases '}
              <CaseLinkList item={item} onOpen={onOpenCase} />
              {item.cases.length === 1 ? ' is' : ' are'} blocked — {String(item.data.reason)}.
            </>
          );
        case 'scheduling_stuck':
          return (
            <>
              {item.cases.length === 1 ? 'Case ' : 'Cases '}
              <CaseLinkList item={item} onOpen={openScheduling} />
              {item.cases.length === 1 ? ' is' : ' are'} having trouble finding a time — please coordinate with them
              directly.
            </>
          );
        case 'scheduling_timed_out':
          return (
            <>
              Scheduling timed out on {item.cases.length === 1 ? 'case ' : 'cases '}
              <CaseLinkList item={item} onOpen={openScheduling} /> — restart it or reach out.
            </>
          );
        case 'trial_soon':
          return (
            <>
              Case <CaseLink id={one.id} label={one.label} onOpen={onOpenCase} /> has a trial in {item.data.days} day
              {Number(item.data.days) === 1 ? '' : 's'} and no mediation booked.
            </>
          );
        case 'party_silent':
          return (
            <>
              No response in {item.data.longest_days}+ days on {item.cases.length === 1 ? 'case ' : 'cases '}
              <CaseLinkList item={item} onOpen={onOpenCase} />.
            </>
          );
        case 'unpaid_invoice':
          return (
            <>
              Unpaid invoice{item.cases.length === 1 ? '' : 's'} on {item.cases.length === 1 ? 'case ' : 'cases '}
              <CaseLinkList item={item} onOpen={onOpenCase} />.
            </>
          );
        case 'coa_ready':
          return (
            <>
              Certificate of Attempts ready on {item.cases.length === 1 ? 'case ' : 'cases '}
              <CaseLinkList item={item} onOpen={onOpenCase} />.
            </>
          );
        case 'unassigned_awaiting':
          return (
            <>
              {item.data.count} unassigned case{Number(item.data.count) === 1 ? '' : 's'} ha
              {Number(item.data.count) === 1 ? 's' : 've'} messages waiting with no owner —{' '}
              {onShowUnassigned ? (
                <button type="button" className="text-brand underline underline-offset-2" onClick={onShowUnassigned}>
                  review them
                </button>
              ) : (
                'review them'
              )}
              .
            </>
          );
      }
    },
    [onOpenCase, openMessages, openScheduling, onShowUnassigned],
  );

  const sentence = useMemo(
    () => (overview ? sessionsSentence(overview.sessions_today, overview.timezone) : null),
    [overview],
  );

  const items = overview?.items ?? [];
  const visible = showAll ? items : items.slice(0, VISIBLE_ITEMS);
  const hiddenCount = items.length - visible.length;

  return (
    <div className="card p-4">
      <div className="text-base font-semibold text-gray-900">Hi {firstName}</div>
      {error ? (
        <div className="mt-1 text-sm text-gray-600">
          {error}{' '}
          <button type="button" className="text-brand underline underline-offset-2" onClick={load}>
            Retry
          </button>
        </div>
      ) : !overview ? (
        <div className="mt-1 text-sm text-gray-400">Pulling together your day…</div>
      ) : (
        <div className="mt-1 space-y-1.5 text-sm leading-relaxed text-gray-800">
          {sentence && <p>{sentence}</p>}
          {visible.length > 0 && (
            <ul className="space-y-1">
              {visible.map((item, idx) => (
                <li key={`${item.type}-${idx}`} className="flex gap-2">
                  <span aria-hidden className="select-none text-gray-300">
                    •
                  </span>
                  <span>{renderItem(item)}</span>
                </li>
              ))}
            </ul>
          )}
          {hiddenCount > 0 && (
            <button type="button" className="text-xs text-brand hover:underline" onClick={() => setShowAll(true)}>
              ▸ and {hiddenCount} more
            </button>
          )}
          {!sentence && items.length === 0 && (
            <p className="text-gray-600">You’re all caught up — no sessions today and nothing is waiting on you.</p>
          )}
        </div>
      )}
    </div>
  );
}
